import oget from 'oget';
import omit from 'lodash.omit';
import groupBy from 'lodash.groupby';
import parseAthleteDetailsTimed from './parse-athlete-details-timed';
import parseAthleteDetailsScored from './parse-athlete-details-scored';

const groupListByHeatCategory = (list, categories = []) => {
  const groupedList = groupBy(list, ({ category = '' } = {}) => category);

  const keys = Object.keys(groupedList);

  if (keys.length > 1 && categories.length) {
    let fallbackToGenericSort = false;

    const sortedList = categories.map(({ label, key } = {}, i) => {
      if (!groupedList[key]) {
        fallbackToGenericSort = true;
        return null;
      }

      return groupedList[key].map((item) => ({
        ...item,
        category: label || key || keys[i],
      }));
    });

    if (!fallbackToGenericSort) {
      return sortedList;
    }
  }

  return Object.keys(groupedList)
    .map((key) => {
      return groupedList[key];
    })
    .sort((arrayA, arrayB) => {
      const catA = parseInt(oget(arrayA, '[0].category', 0), 10);

      const catB = parseInt(oget(arrayB, '[0].category', 0), 10);

      if (catA === catB) {
        return 0;
      }

      return catA > catB ? -1 : +1;
    });
};

const parseAthletes = (parseAthleteDetails = (details) => details, startList = [], results = []) => {
  let longestAthleteName = '';

  const athletesById = startList.reduce((obj, { athlete, category, isNext, isLive }) => {
    if (!athlete || !athlete.externalId) {
      return obj;
    }

    const item = {
      ...athlete,
      category,
      isNext,
      isLive,
    };

    const { fullName } = item;
    if (typeof fullName === 'string' && longestAthleteName.length < fullName.length) {
      longestAthleteName = fullName;
    }

    obj[athlete.externalId] = item;

    const additionalBio = omit(athlete, [
      'fullName',
      'shortName',
      'doB',
      'firstName',
      'lastName',
      'nationality',
      'filename',
      'gender',
      'stance',
      'age',
      'homeTown',
      'externalId',
      'thirdPartyId',
    ]);

    obj[athlete.externalId].additionalBio = Object.keys(additionalBio)
      .reduce(
        (arr, key) => [
          ...arr,
          {
            label: key,
            value: additionalBio[key],
          },
        ],
        [],
      )
      .filter((item) => !!item && parseFloat(item, 10) !== 0);

    return obj;
  }, {});

  let labelsCount = 0;
  let hasTricks = false;
  let mostRunsCount = 0;
  let mostTricksCount = 0;
  let longestScore = '';
  let testAthlete = {};

  results.forEach((result) => {
    const { externalAthleteId, details, ...item } = result;

    if (!athletesById[externalAthleteId]) {
      return;
    }

    const startListItem = athletesById[externalAthleteId];

    athletesById[externalAthleteId] = {
      ...startListItem,
      ...item,
      isNext: startListItem.isNext || item.isNext,
      isLive: startListItem.isLive || item.isLive,
    };

    const parsedDetails = parseAthleteDetails(details);

    athletesById[externalAthleteId].details = parsedDetails;
    athletesById[externalAthleteId].hasExtraScores = Array.isArray(oget(parsedDetails, 'Runs.values[0].judges'));

    hasTricks = hasTricks || !!parsedDetails.Tricks;

    const scoreTypesCount = Object.keys(parsedDetails).length;

    labelsCount = labelsCount > scoreTypesCount ? labelsCount : scoreTypesCount;

    if (Array.isArray(oget(parsedDetails, 'Runs.values')) && parsedDetails.Runs.values.length > mostRunsCount) {
      mostRunsCount = parsedDetails.Runs.values.length;
    }
    if (Array.isArray(oget(parsedDetails, 'Tricks.values')) && parsedDetails.Tricks.values.length > mostTricksCount) {
      mostTricksCount = parsedDetails.Tricks.values.length;
    }

    Object.keys(parsedDetails).forEach((key) => {
      if (key === 'ColumnInformation') {
        const value = oget(parsedDetails, `[${key}].values[0].Value`, []);

        if (longestScore.length < value.length) {
          longestScore = value;
        }
      } else if (key === 'Runs' || key === 'Tricks' || key === 'Needs') {
        const values = oget(parsedDetails, `[${key}].values`, []);

        values.forEach(({ rank = '' }) => {
          if (longestScore.length < rank.length) {
            longestScore = rank;
          }
        });
      }
    });

    if (typeof result.value === 'string' && longestScore.length < result.value.length) {
      longestScore = result.value;
    }

    testAthlete = athletesById[externalAthleteId];
  });

  return {
    testData: {
      longestAthleteName,
      longestScore: ''.padStart(longestScore.length, '0'),
      testAthlete,
    },
    info: {
      labelsCount,
      hasTricks,
      mostRunsCount,
      mostTricksCount,
    },
    athletesById,
  };
};

const parseHeat = (roundType, heat = {}) => {
  if (heat.__parsed__) {
    return heat;
  }

  const startList = oget(heat, 'startList', []);

  const results = oget(heat, 'results', []);

  const { testData, info, athletesById } = parseAthletes(
    roundType === 'Time' ? parseAthleteDetailsTimed : parseAthleteDetailsScored,
    startList,
    results,
  );

  return {
    __parsed__: true,
    ...heat,
    testData,
    info,
    startList: groupListByHeatCategory(startList, heat.categories),
    results: groupListByHeatCategory(results, heat.categories),
    athletesById,
  };
};

export default parseHeat;
